import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Progress,
  Table,
  Badge,
  Row,
  Col
} from 'reactstrap';
import axios from 'axios';

const ScanUpload = ({ sspId: propSspId, onScanComplete }) => {
  const [files, setFiles] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // SSP selection state
  const [ssps, setSSPs] = useState([]);
  const [selectedSspId, setSelectedSspId] = useState(propSspId || '');

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
  const MAX_FILES = 20;

  // Use prop sspId if provided, otherwise use local state
  const sspId = propSspId || selectedSspId;

  useEffect(() => {
    fetchMappings();
    fetchSSPs();
  }, []);

  useEffect(() => {
    if (propSspId) {
      setSelectedSspId(propSspId);
    }
  }, [propSspId]);

  const fetchSSPs = async () => {
    try {
      const response = await axios.get(`${baseURL}/ssp-studio/ssps`, {
        withCredentials: true
      });
      if (response.data.success) {
        setSSPs(response.data.data);
        // Auto-select first SSP if none selected
        if (!propSspId && !selectedSspId && response.data.data.length > 0) {
          setSelectedSspId(response.data.data[0]._id);
        }
      }
    } catch (err) {
      console.error('Error fetching SSPs:', err);
    }
  };

  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${baseURL}/ssp-studio/mappings`, {
        withCredentials: true
      });
      if (response.data.success) {
        setMappings(response.data.data);
        // Auto-select active mapping
        const active = response.data.data.find(m => m.isActive);
        if (active) {
          setSelectedMapping(active._id);
        }
      }
    } catch (err) {
      console.error('Error fetching mappings:', err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed. Selected: ${selectedFiles.length}`);
      return;
    }
    if (selectedFiles.length > 0) {
      setFiles(selectedFiles);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    if (!sspId) {
      setError('SSP ID is required');
      return;
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append('scanFiles', file);
    });
    formData.append('sspId', sspId);
    if (selectedMapping) {
      formData.append('mappingId', selectedMapping);
    }

    try {
      setUploading(true);
      setProgress(30);
      setError(null);

      const response = await fetch(`${baseURL}/ssp-studio/scans/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      setProgress(70);

      const data = await response.json();

      if (data.success) {
        setProgress(100);
        setResult(data.data);
        setFiles([]);
        // Reset file input
        document.getElementById('scanFileInput').value = '';

        // Call parent callback if provided
        if (onScanComplete) {
          onScanComplete(data.data);
        }
      } else {
        setError(data.message || 'Failed to upload scans');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during upload');
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <i className="mdi mdi-file-upload"></i> Upload Security Scan
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleUpload}>
            {/* SSP Selection - only show if no SSP is pre-selected via props */}
            {!propSspId && (
              <FormGroup>
                <Label for="sspSelect">
                  Select SSP <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="sspSelect"
                  value={selectedSspId}
                  onChange={(e) => setSelectedSspId(e.target.value)}
                  disabled={uploading}
                  required
                >
                  <option value="">-- Select an SSP --</option>
                  {ssps.map(ssp => (
                    <option key={ssp._id} value={ssp._id}>
                      {ssp.name} ({ssp.metadata?.systemName || 'No system name'})
                    </option>
                  ))}
                </Input>
                <small className="form-text text-muted">
                  Scans will be associated with this SSP
                </small>
              </FormGroup>
            )}

            <FormGroup>
              <Label for="mappingSelect">Mapping Set</Label>
              <Input
                type="select"
                id="mappingSelect"
                value={selectedMapping}
                onChange={(e) => setSelectedMapping(e.target.value)}
                disabled={uploading}
              >
                <option value="">Use active mapping</option>
                {mappings.map(mapping => (
                  <option key={mapping._id} value={mapping._id}>
                    {mapping.name} (v{mapping.version})
                    {mapping.isActive ? ' - Active' : ''}
                  </option>
                ))}
              </Input>
              <small className="form-text text-muted">
                Mapping defines how scan fields map to NIST controls and SSP sections
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="scanFileInput">Scan Files (up to {MAX_FILES})</Label>
              <Input
                type="file"
                id="scanFileInput"
                onChange={handleFileChange}
                disabled={uploading}
                accept=".json,.csv,.xml,.sarif,.html,.pdf,.txt"
                multiple
              />
              <small className="form-text text-muted">
                Supported formats: JSON, CSV, XML, SARIF, HTML, PDF, TXT (max 50MB per file, up to 20 files)
              </small>
            </FormGroup>

            {files.length > 0 && (
              <Alert color="info">
                <strong>Selected {files.length} file(s):</strong>
                <ul className="mb-0 mt-2">
                  {files.map((file, idx) => (
                    <li key={idx}>
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </li>
                  ))}
                </ul>
                <div className="mt-2">
                  <strong>Total size:</strong> {(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                </div>
              </Alert>
            )}

            {uploading && (
              <div className="mb-3">
                <Progress value={progress} animated />
                <p className="text-center text-muted mt-2">
                  Processing scan file...
                </p>
              </div>
            )}

            {error && (
              <Alert color="danger" toggle={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              color="primary"
              disabled={files.length === 0 || uploading || !sspId}
              block
            >
              {uploading ? 'Processing...' : `Upload and Process ${files.length > 0 ? files.length : ''} Scan${files.length !== 1 ? 's' : ''}`}
            </Button>
          </Form>
        </CardBody>
      </Card>

      {result && (
        <Card className="mt-3">
          <CardHeader className="bg-success text-white">
            <i className="mdi mdi-check-circle"></i> {result.filesProcessed || 1} Scan File(s) Processed Successfully
          </CardHeader>
          <CardBody>
            {result.fileResults && result.fileResults.length > 1 && (
              <div className="mb-3">
                <h6>Files Processed:</h6>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Fields</th>
                      <th>Matched</th>
                      <th>Findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.fileResults.map((fileResult, idx) => (
                      <tr key={idx}>
                        <td>{fileResult.filename}</td>
                        <td>{fileResult.stats.totalFields}</td>
                        <td>{fileResult.stats.matchedFields}</td>
                        <td>{fileResult.findingsCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <Row>
              <Col md={6}>
                <h6>Aggregate Statistics</h6>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td><strong>Files Processed:</strong></td>
                      <td>{result.filesProcessed || 1}</td>
                    </tr>
                    <tr>
                      <td><strong>Total Fields:</strong></td>
                      <td>{result.stats.totalFields}</td>
                    </tr>
                    <tr>
                      <td><strong>Matched Fields:</strong></td>
                      <td>{result.stats.matchedFields}</td>
                    </tr>
                    <tr>
                      <td><strong>Findings Generated:</strong></td>
                      <td>{result.stats.findingsGenerated}</td>
                    </tr>
                    <tr>
                      <td><strong>POA&Ms Created:</strong></td>
                      <td>{result.stats.poamsCreated}</td>
                    </tr>
                    <tr>
                      <td><strong>Controls Updated:</strong></td>
                      <td>{result.stats.controlsUpdated}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={6}>
                <h6>Findings by Severity</h6>
                <Table size="sm" borderless>
                  <tbody>
                    {result.stats.severityBreakdown && Object.entries(result.stats.severityBreakdown).map(([severity, count]) => (
                      <tr key={severity}>
                        <td>
                          <Badge color={getSeverityColor(severity)}>
                            {severity.toUpperCase()}
                          </Badge>
                        </td>
                        <td><strong>{count}</strong> findings</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {result.stats.poamsCreated > 0 && (
              <Alert color="warning" className="mt-3">
                <i className="mdi mdi-alert"></i>
                <strong> {result.stats.poamsCreated} POA&M items</strong> were automatically created for high/critical findings.
                Review them in the POA&M tab.
              </Alert>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default ScanUpload;
